import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  TextField,
  Typography,
  Collapse,
  List,
  ListItemButton,
  ListItemText,
  Button,
  ListItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import {
  MealName,
  MealNameToOrder,
  MenuItem,
  Recipe,
  State,
  StateProps,
} from "../state";
import { openMealCard } from "../MealDisplay";
import { loadRecipes, writeRecipe } from "../../api";
import { localStorageRecipeKey } from "../../App";
import CheckBox from "../base/CheckBox";

export interface RunSearchProps extends StateProps {
  recipes: Record<string, Recipe>;
  readonly searchTerm: string;
  readonly setSearchTerm: React.Dispatch<React.SetStateAction<string>>;
}

interface SearchScore {
  readonly recipeName: string;
  readonly searchScore: number;
}

interface FuzzySearchProps {
  readonly query: string;
  readonly target: string;
}
export function fuzzySearch(props: FuzzySearchProps) {
  const { query, target } = props;
  const queryLowerCase = query.toLowerCase();
  const targetLowerCase = target.toLowerCase();

  // Create a matrix to store the results of comparisons
  const matrix = [];
  const maxDistance = Math.max(queryLowerCase.length, targetLowerCase.length);

  // Initialize the matrix
  for (let i = 0; i <= queryLowerCase.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= targetLowerCase.length; j++) {
    matrix[0][j] = j;
  }

  // Fill in the matrix
  for (let i = 1; i <= queryLowerCase.length; i++) {
    for (let j = 1; j <= targetLowerCase.length; j++) {
      const cost = queryLowerCase[i - 1] === targetLowerCase[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1, // Deletion
        matrix[i][j - 1] + 1, // Insertion
        matrix[i - 1][j - 1] + cost, // Substitution
      );
    }
  }

  const customAddition =
    (target.slice(0, query.length) === query ? -2 : 0) * query.length;

  // Calculate a similarity score based on the matrix
  const similarity =
    1 - (matrix[query.length][target.length] + customAddition) / maxDistance;

  return similarity;
}

function runSearch(props: RunSearchProps) {
  const { searchTerm, recipes, setState, setSearchTerm } = props;
  setSearchTerm(searchTerm);
  const recipesToSearch = Object.values(recipes ?? {}).map((x) => {
    return x.name;
  });
  const searchResultValues: SearchScore[] = [];
  for (let i = 0; i < recipesToSearch.length; i++) {
    searchResultValues.push({
      recipeName: recipesToSearch[i],
      searchScore: fuzzySearch({
        query: searchTerm,
        target: recipesToSearch[i],
      }),
    });
  }
  const searchResults = searchResultValues
    .sort((a, b) => (a.searchScore > b.searchScore ? -1 : 1))
    .map((r) => r.recipeName);
  setState((prevState: State) => {
    return {
      ...prevState,
      searchResults,
    };
  });
}

export interface SearchBoxProps extends StateProps {
  recipes: Record<string, Recipe>;
}
export function SearchBox(props: SearchBoxProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const { state, setState, recipes } = props;

  return (
    <>
      <TextField
        placeholder="search"
        multiline={false}
        fullWidth
        variant="filled"
        value={searchTerm}
        sx={{
          paddingBottom: "2em",
        }}
        onChange={(e) =>
          runSearch({
            searchTerm: e.target.value,
            recipes,
            state,
            setState,
            setSearchTerm,
          })
        }
      />
      <Box mt={3}>
        <Collapse in={searchTerm !== ""} timeout="auto" unmountOnExit>
          <List
            dense
            sx={{
              maxHeight: 200,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            {/* TODO - repeated in expandableList */}
            {(state.searchResults ?? []).map((item) => (
              <ListItem sx={{ display: "flex", alignItems: "center" }}>
                <ListItemButton
                  sx={{ flexGrow: 1 }}
                  onClick={() => {
                    onClick({
                      state,
                      setState,
                      item: recipes?.[item],
                    });
                  }}
                  key={`${item}-search`}
                >
                  <ListItemText primary={item} />
                </ListItemButton>
                {/* TODO - icon button: edit recipe */}
                <IconButton
                  onClick={() =>
                    openMealCard({
                      ...props,
                      selectedRecipe: recipes?.[item],
                    })
                  }
                  edge="end"
                  aria-label="view"
                >
                  <VisibilityIcon />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </Box>
    </>
  );
}

export interface RecipeBookModalProps extends StateProps {
  readonly setSaveRecipeSnackbarOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  readonly setSaveRecipeSnackbarText?: React.Dispatch<
    React.SetStateAction<string>
  >;
}

export function RecipeBookModal(props: RecipeBookModalProps) {
  const [expanded, setExpanded] = useState(false);
  const [recipes, setRecipes] = useState<Record<string, Recipe>>(
    JSON.parse(localStorage.getItem(localStorageRecipeKey) ?? "{}"),
  );
  const [customRecipeExpanded, setCustomRecipeExpanded] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const loadedRecipes = await loadRecipes();
      if (Object.keys(loadedRecipes).length > 0) {
        localStorage.setItem(
          localStorageRecipeKey,
          JSON.stringify(loadedRecipes),
        );
        setRecipes(loadedRecipes);
      }
    };

    fetchData();
  }, [expanded]);

  const { state, setState } = props;
  return (
    <Box
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "95%",
        height: "80%",
        bgcolor: "background.paper",
        borderRadius: 2,
        boxShadow: 24,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 3,
          pb: 2,
        }}
      >
        {SearchBox({ ...props, recipes })}
        <Box mt={2}>
          <Typography
            display={
              Object.values(state.menu)
                .map((menuItem) => {
                  return menuItem.selected;
                })
                .some((i) => {
                  return i;
                })
                ? "block"
                : "none"
            }
            variant="subtitle1"
            fontWeight="bold"
          >
            Selected:{" "}
            <span style={{ fontWeight: "normal" }}>
              {" "}
              {Object.values(state.menu).map((menuItem) => {
                if (menuItem.selected) {
                  return menuItem.recipe?.name ?? "";
                }
              }) ?? ""}{" "}
            </span>
          </Typography>
        </Box>

        {/* Expandable list */}
        {expandableList({
          expanded,
          setExpanded,
          state,
          setState,
          recipes,
        })}
        {CustomRecipe({
          expanded: customRecipeExpanded,
          setExpanded: setCustomRecipeExpanded,
          state,
          setState,
          recipes,
          setSaveRecipeSnackbarOpen: props.setSaveRecipeSnackbarOpen,
          setSaveRecipeSnackbarText: props.setSaveRecipeSnackbarText,
        })}
      </Box>
      <Box
        sx={{
          p: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Button
          variant="contained"
          onClick={() => {
            Object.entries(state.menu).map(([date, menuItem]) => {
              if (menuItem.selected) {
                setState((prevState: State) => {
                  const prevMenu = JSON.parse(JSON.stringify(prevState.menu));
                  prevMenu[date].recipe = undefined;
                  return {
                    ...prevState,
                    menu: prevMenu,
                    openModals: {
                      ...prevState.openModals,
                      recipeBook: false,
                    },
                  };
                });
              }
            });
          }}
          sx={{
            display:
              Object.values(state.menu).map((menuItem) => {
                if (menuItem.selected) {
                  return "inline-flex";
                }
              })[0] ?? "None",
            px: 3,
            py: 0.5,
            fontSize: "0.85rem",
            borderRadius: 3,
            textTransform: "none",
            background: `linear-gradient(
        to bottom right,
        rgba(255, 255, 255, 0.25),
        rgba(255, 255, 255, 0.05)
      )`,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            border: "1px solid rgba(255, 255, 255, 0.18)",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            color: "text.primary",
            "&:hover": {
              background: `linear-gradient(
          to bottom right,
          rgba(255, 255, 255, 0.35),
          rgba(255, 255, 255, 0.15)
        )`,
            },
          }}
        >
          Remove
        </Button>
      </Box>
    </Box>
  );
}

export interface ExpandableListProps extends StateProps {
  recipes: Record<string, Recipe>;
  readonly expanded: boolean;
  readonly setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
  readonly setSaveRecipeSnackbarOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  readonly setSaveRecipeSnackbarText?: React.Dispatch<
    React.SetStateAction<string>
  >;
}

const blankCustomRecipeState = {
  title: "",
  ingredients: "",
  instructions: "",
};
export function CustomRecipe(props: ExpandableListProps) {
  const [customRecipeState, setCustomRecipeState] = useState(
    blankCustomRecipeState,
  );
  const { expanded, setExpanded, state, setState } = props;
  // const checked = true;
  const [checked, setChecked] = useState(true);
  const [checkedSaveRecipe, setCheckedSaveRecipe] = useState(true);
  return (
    <Box mt={3}>
      <ListItemButton onClick={() => setExpanded(!expanded)}>
        <ListItemText primary="Custom Recipe" />
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {Object.keys(customRecipeState).map((k) => {
          return (
            <TextField
              fullWidth
              multiline={
                ["ingredients", "instructions"].includes(k) ? true : false
              }
              rows={4}
              sx={{ display: "block" }}
              id="outlined-basic"
              label={k}
              value={customRecipeState[k as keyof typeof customRecipeState]}
              onChange={(e) => {
                setCustomRecipeState((prevCustomRecipe) => {
                  return {
                    ...prevCustomRecipe,
                    [k as keyof typeof customRecipeState]: e.target.value,
                  };
                });
              }}
              variant="outlined"
            />
          );
        })}
        {CheckBox({
          state: [checked, setChecked],
          label: "Add to shopping list (each new line is an item)",
        })}
        {CheckBox({
          state: [checkedSaveRecipe, setCheckedSaveRecipe],
          label: "Save as new recipe",
        })}
        <Button
          sx={{
            marginTop: "2.5%",
          }}
          onClick={() => {
            const ingredients = customRecipeState.ingredients
              .toLocaleLowerCase()
              .split("\n")
              .filter((y) => {
                return y !== "";
              })
              .map((x) => {
                return {
                  name: x,
                  showAsShoppingListItem: true,
                };
              });
            const instructions = [
              ...customRecipeState.instructions.toLocaleLowerCase().split("\n"),
            ].filter((val): val is string => val.trim() !== "");
            const customRecipe: Recipe = {
              instructions: [
                {
                  ingredients,
                  instructions:
                    instructions.length > 0 ? instructions : ["cook it"],
                },
              ],
              name: `custom ${customRecipeState.title ?? state.selectedMealName}`,
            };
            onClick({
              addToShoppingList: checked,
              item: customRecipe,
              saveAsNewRecipe: checkedSaveRecipe,
              setCustomRecipeState,
              setSaveRecipeSnackbarOpen: props.setSaveRecipeSnackbarOpen,
              setSaveRecipeSnackbarText: props.setSaveRecipeSnackbarText,
              setState,
              state,
            });
          }}
          variant="outlined"
        >
          Submit
        </Button>
      </Collapse>
    </Box>
  );
}

export function expandableList(props: ExpandableListProps) {
  const { expanded, setExpanded, state, setState } = props;
  return (
    <Box mt={3}>
      <ListItemButton onClick={() => setExpanded(!expanded)}>
        <ListItemText primary="Recipes" />
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List
          dense
          sx={{
            maxHeight: 200,
            overflowY: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          {Object.values(props.recipes ?? {}).map((item, i) => (
            <ListItem sx={{ display: "flex", alignItems: "center" }}>
              <ListItemButton
                sx={{ flexGrow: 1 }}
                onClick={() => {
                  onClick({
                    state,
                    setState,
                    item,
                  });
                }}
                key={`${item}-${i}`}
              >
                <ListItemText primary={item.name} />
              </ListItemButton>
              <IconButton
                onClick={() =>
                  openMealCard({
                    ...props,
                    selectedRecipe: item,
                  })
                }
                edge="end"
                aria-label="view"
              >
                <VisibilityIcon />
              </IconButton>
            </ListItem>
          ))}
        </List>
      </Collapse>
    </Box>
  );
}

interface OnClickProps extends StateProps {
  readonly item: Recipe | undefined;
  readonly setCustomRecipeState?: React.Dispatch<
    React.SetStateAction<{
      title: string;
      ingredients: string;
      instructions: string;
    }>
  >;
  readonly addToShoppingList?: boolean;
  readonly saveAsNewRecipe?: boolean;
  readonly setSaveRecipeSnackbarOpen?: React.Dispatch<
    React.SetStateAction<boolean>
  >;
  readonly setSaveRecipeSnackbarText?: React.Dispatch<
    React.SetStateAction<string>
  >;
}
export async function onClick(props: OnClickProps) {
  const {
    state,
    setState,
    item,
    addToShoppingList,
    saveAsNewRecipe,
    setCustomRecipeState,
    setSaveRecipeSnackbarOpen,
    setSaveRecipeSnackbarText,
  } = props;
  // TODO - remove from additional items
  console.log({ saveAsNewRecipe, item });
  if (saveAsNewRecipe && item) {
    if (!item.name.startsWith("custom ")) {
      await writeRecipe({ ...item });
    } else {
      if (setSaveRecipeSnackbarText && setSaveRecipeSnackbarOpen) {
        setSaveRecipeSnackbarText(
          "failed to save recipe - recipe needs a name",
        );
        setSaveRecipeSnackbarOpen(true);
      }
    }
  }
  if (setCustomRecipeState !== undefined) {
    setCustomRecipeState!(blankCustomRecipeState);
  }

  const updateAdditionalItems =
    addToShoppingList &&
    item !== undefined &&
    item?.instructions.flatMap((x) => {
      x.ingredients !== undefined &&
        x.ingredients.length > 0 &&
        x.ingredients.some((y) => {
          return y.name !== "";
        });
    });
  const stateMenuEntries = Object.entries(state.menu);
  if (stateMenuEntries.length > 0) {
    stateMenuEntries.map(([date, menuItem]) => {
      if (menuItem.selected) {
        setState((prevState: State) => {
          const prevMenu = JSON.parse(JSON.stringify(prevState.menu)) as Record<
            string,
            MenuItem
          >;
          const selectedMealName = state.selectedMealName;
          prevMenu[date].recipe = item;
          prevMenu[date].mealName = selectedMealName;
          prevMenu[date].mealOrder = state.settings
            ? state.settings.meals.indexOf(selectedMealName)
            : (MealNameToOrder[selectedMealName as MealName] ?? 0);
          return {
            ...prevState,
            menu: prevMenu,
            openModals: {
              ...prevState.openModals,
              recipeBook: false,
            },
            selectedRecipe: item,
            additionalItems: [
              ...prevState.additionalItems,
              ...(updateAdditionalItems
                ? (item?.instructions ?? [])
                : []
              ).flatMap((instruction) => {
                return (instruction.ingredients ?? []).map((ingredient) => {
                  return ingredient.name;
                });
              }),
            ],
          };
        });
      }
    });
  } else {
    setState((prevState: State) => {
      return {
        ...prevState,
        selectedRecipe: item,
      };
    });
  }
}
